import {ReactNode} from "react";
import {OtsTooltip} from "./OtsTooltip";

export type OtsStepDot = {
  id: string;
  label: string;
  done: boolean;
  /** Who or when, shown in the tooltip beside the label. */
  detail?: ReactNode;
  /** Color of the dot once done, so different sign-offs read apart at a glance. Defaults to success. */
  tone?: OtsStepDotTone;
};

export type OtsStepDotTone = "success" | "neutral" | "blue" | "indigo" | "violet" | "danger";

const doneClasses: Record<OtsStepDotTone, string> = {
  success: "border-[var(--ots-success)] bg-[var(--ots-success)] text-white",
  neutral: "border-[var(--ots-text-muted)] bg-[var(--ots-text-muted)] text-[var(--ots-surface)]",
  blue: "border-[var(--ots-blue)] bg-[var(--ots-blue)] text-white",
  indigo: "border-[var(--ots-indigo)] bg-[var(--ots-indigo)] text-white",
  violet: "border-[var(--ots-violet)] bg-[var(--ots-violet)] text-white",
  danger: "border-[var(--ots-error)] bg-[var(--ots-error)] text-[var(--ots-on-error)]",
};

export type OtsStepDotsProps = {
  steps: OtsStepDot[];
  /** What the steps are, plural, for the accessible summary. */
  noun?: string;
  className?: string;
};

/** A row-sized summary of a few sign-offs: one lettered dot per step, filled once done. */
export const OtsStepDots = ({steps, noun = "steps", className}: OtsStepDotsProps) => {
  if (!steps.length) return null;
  const done = steps.filter(step => step.done).length;

  return <OtsTooltip
    density="compact"
    content={<ul className="space-y-0.5">
      {steps.map(step => <li key={step.id}>{step.label}: {step.detail ?? (step.done ? "done" : "not done")}</li>)}
    </ul>}>
    <span
      tabIndex={0}
      aria-label={`${done} of ${steps.length} ${noun} complete`}
      className={["inline-flex gap-0.5", className].filter(Boolean).join(" ")}>
      {steps.map(step => <span
        key={step.id}
        aria-hidden="true"
        data-done={step.done}
        data-tone={step.done ? step.tone ?? "success" : undefined}
        className={[
          "flex size-4 items-center justify-center rounded-full border text-[9px] font-bold leading-none",
          step.done
            ? doneClasses[step.tone ?? "success"]
            : "border-[var(--ots-outline-variant)] text-[var(--ots-text-muted)]",
        ].join(" ")}>
        {step.label[0]}
      </span>)}
    </span>
  </OtsTooltip>;
};
